import { useEffect, useState } from "react";
import { subscribeUpdate, isUpdateAvailable, applyUpdateNow } from "../lib/app-update.js";
import { pendingWrites, isSafeToReplaceApp } from "../lib/app-busy.js";

/**
 * Offers a refresh when a new build is waiting and the app cannot safely take it
 * on its own (student mid-drill, or an answer still saving).
 *
 * Mounted once at the app root, next to SyncErrorToast.
 *
 * It stays SILENT when the update can be applied automatically — on a browsing
 * screen with nothing in flight, sw-register reloads the moment the tab is
 * hidden and the student never sees this. The prompt exists for the case that
 * kept tabs on stale code for days: a long session that never reaches a quiet
 * moment.
 */
export function UpdateBanner() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const evaluate = () => {
      // If it is safe right now, sw-register will take it silently on the next
      // hide. Only ask when asking is the only way through.
      if (isUpdateAvailable() && !isSafeToReplaceApp()) setShow(true);
    };
    evaluate();
    const un = subscribeUpdate(evaluate);
    // Re-evaluate as the student moves around: an update that arrived mid-drill
    // should stop nagging once they leave it, because the silent path can take
    // over from there.
    const onNav = () => {
      if (isUpdateAvailable() && isSafeToReplaceApp()) setShow(false);
      else evaluate();
    };
    window.addEventListener("popstate", onNav);
    const poll = setInterval(onNav, 5000);
    return () => {
      un();
      window.removeEventListener("popstate", onNav);
      clearInterval(poll);
    };
  }, []);

  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        bottom: "calc(88px + env(safe-area-inset-bottom))",
        transform: "translateX(-50%)",
        zIndex: 60,
        // A DEFINITE WIDTH, not just a maximum. This was `maxWidth` alone, and a
        // fixed-position box with no width shrinks to fit its contents: the two
        // buttons held their minWidths, the text had flex-basis 0, and so the
        // sentence collapsed to its longest word and rendered one word per line
        // down the middle of the screen, on top of the answer choices.
        width: "min(92vw, 420px)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 8,
        background: "var(--bg-1)",
        border: "1px solid var(--border-1)",
        boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
        fontSize: 13,
        color: "var(--fg-0)",
        lineHeight: 1.4,
      }}
    >
      {/* minWidth: 0 lets a flex item shrink below its longest word instead of
          forcing the row wider, which is the other half of the same failure. */}
      <span style={{ flex: 1, minWidth: 0 }}>
        A new version is ready. Finish this question, then refresh.
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void applyUpdateNow(pendingWrites);
        }}
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--border-1)",
          borderRadius: 6,
          color: "var(--fg-0)",
          cursor: busy ? "default" : "pointer",
          fontSize: 13,
          padding: "6px 12px",
          minHeight: 44,
          minWidth: 80,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {busy ? "Saving…" : "Refresh"}
      </button>
      <button
        type="button"
        onClick={() => setShow(false)}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          color: "var(--fg-3)",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
          padding: 4,
          minHeight: 44,
          minWidth: 44,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
